import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, STATUSES, CURRENCIES, SALARY_PERIODS, EMPLOYMENT_TYPES, PRIORITIES } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { ChevronLeft, Trash2, Wand2, Loader2 } from "lucide-react";

const empty = {
  title: "", company: "", location: "", url: "",
  salary_min: "", salary_max: "", currency: "USD", salary_period: "year",
  employment_type: "", remote: false,
  notes: "", description: "", status: "saved", priority: "medium",
};

export default function JobForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [parseUrl, setParseUrl] = useState("");
  const [parsing, setParsing] = useState(false);
  const editing = Boolean(id);

  useEffect(() => {
    if (editing) return;
    const incoming = searchParams.get("url");
    if (incoming) {
      setParseUrl(incoming);
      autofill(incoming);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  useEffect(() => {
    if (!editing) return;
    (async () => {
      try {
        const r = await api.get(`/jobs/${id}`);
        setForm({
          ...empty,
          ...r.data,
          salary_min: r.data.salary_min ?? "",
          salary_max: r.data.salary_max ?? "",
          currency: r.data.currency || "USD",
          salary_period: r.data.salary_period || "year",
          employment_type: r.data.employment_type || "",
        });
      } catch {
        toast.error("Could not load job");
        navigate("/jobs");
      }
    })();
  }, [id, editing, navigate]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const toggleRemote = (checked) =>
    setForm({ ...form, remote: !!checked, location: checked ? "" : form.location });

  const autofill = async (overrideUrl) => {
    const target = (typeof overrideUrl === "string" ? overrideUrl : parseUrl).trim();
    if (!target) { toast.error("Paste a job listing URL first"); return; }
    setParseUrl(target);
    setParsing(true);
    try {
      const r = await api.post("/jobs/parse-url", { url: target });
      const d = r.data;
      setForm((f) => ({
        ...f,
        title: d.title || f.title,
        company: d.company || f.company,
        location: d.remote ? "" : (d.location || f.location),
        remote: d.remote ?? f.remote,
        url: d.url || target,
        salary_min: d.salary_min ?? f.salary_min,
        salary_max: d.salary_max ?? f.salary_max,
        currency: d.currency || f.currency,
        salary_period: d.salary_period || f.salary_period,
        employment_type: d.employment_type || f.employment_type,
        description: d.description || f.description,
      }));
      if (d.partial) {
        toast.warning("We could only read limited info from this page (it may require JavaScript). Please review and complete the details manually.");
      } else {
        toast.success("Details auto-filled — review before saving");
      }
    } catch (e) {
      if (e.response?.status === 429) toast.warning(e.response.data.detail);
      else toast.error(e.response?.data?.detail || "Could not parse that URL");
    } finally {
      setParsing(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.company) {
      toast.error("Title and company are required");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      salary_min: form.salary_min === "" ? null : Number(form.salary_min),
      salary_max: form.salary_max === "" ? null : Number(form.salary_max),
      employment_type: form.employment_type || null,
    };
    try {
      if (editing) {
        await api.patch(`/jobs/${id}`, payload);
        toast.success("Job updated");
        navigate(`/jobs/${id}`);
      } else {
        const r = await api.post("/jobs", payload);
        toast.success("Job saved");
        navigate(`/jobs/${r.data.job_id}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!editing) return;
    if (!window.confirm("Delete this job?")) return;
    await api.delete(`/jobs/${id}`);
    toast.success("Deleted");
    navigate("/jobs");
  };

  return (
    <div className="max-w-2xl mx-auto" data-testid="job-form-page">
      <button onClick={() => navigate(-1)} className="label-tiny inline-flex items-center mb-4 hover:text-zinc-900" data-testid="back-btn">
        <ChevronLeft size={14} className="mr-1" /> Back
      </button>
      <h1 className="font-heading text-3xl font-black tracking-tight">
        {editing ? "Edit job" : "New job"}
      </h1>
      <p className="text-zinc-600 mt-2">Track a role from listing to offer.</p>

      {!editing && (
        <div className="mt-6 border-2 border-zinc-900 p-4" data-testid="parse-url-box">
          <Label className="label-tiny block mb-2">Paste a job URL to auto-fill</Label>
          <div className="flex gap-2">
            <Input
              data-testid="parse-url-input"
              value={parseUrl}
              onChange={(e) => setParseUrl(e.target.value)}
              placeholder="https://company.com/careers/role"
              className="rounded-none h-11"
            />
            <Button type="button" onClick={() => autofill()} disabled={parsing} data-testid="parse-url-btn" className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white h-11 px-5 shrink-0">
              {parsing ? <Loader2 size={16} className="mr-1.5 animate-spin" /> : <Wand2 size={16} className="mr-1.5" />}
              {parsing ? "Reading…" : "Auto-fill"}
            </Button>
          </div>
          <p className="text-xs text-zinc-500 mt-2">We fetch the page and use AI to extract the details. Always review before saving.</p>
        </div>
      )}

      <form onSubmit={submit} className="mt-8 space-y-5">
        <Field label="Title *">
          <Input data-testid="form-title" value={form.title} onChange={set("title")} placeholder="Senior Backend Engineer" className="rounded-none h-11" />
        </Field>
        <Field label="Company *">
          <Input data-testid="form-company" value={form.company} onChange={set("company")} placeholder="Acme Corp" className="rounded-none h-11" />
        </Field>

        <div>
          <label className="flex items-center gap-3 cursor-pointer mb-3" data-testid="form-remote-row">
            <Checkbox checked={form.remote} onCheckedChange={toggleRemote} data-testid="form-remote" className="rounded-none" />
            <span className="text-sm font-medium">Remote role</span>
          </label>
          <Field label="Location">
            <Input
              data-testid="form-location"
              value={form.location}
              onChange={set("location")}
              disabled={form.remote}
              placeholder={form.remote ? "Remote" : "New York, NY"}
              className="rounded-none h-11 disabled:bg-zinc-100 disabled:text-zinc-400"
            />
          </Field>
        </div>

        <div>
          <Label className="label-tiny block mb-2">Salary</Label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Input data-testid="form-salary-min" type="number" value={form.salary_min} onChange={set("salary_min")} placeholder="Min" className="rounded-none h-11" />
            <Input data-testid="form-salary-max" type="number" value={form.salary_max} onChange={set("salary_max")} placeholder="Max" className="rounded-none h-11" />
            <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
              <SelectTrigger data-testid="form-currency" className="rounded-none h-11"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CURRENCIES.map((c) => (
                  <SelectItem key={c.code} value={c.code} data-testid={`currency-${c.code}`}>{c.symbol} {c.code}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={form.salary_period} onValueChange={(v) => setForm({ ...form, salary_period: v })}>
              <SelectTrigger data-testid="form-period" className="rounded-none h-11"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SALARY_PERIODS.map((p) => (
                  <SelectItem key={p.key} value={p.key} data-testid={`period-${p.key}`}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          <Field label="Employment type">
            <Select value={form.employment_type || "none"} onValueChange={(v) => setForm({ ...form, employment_type: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="form-employment-type" className="rounded-none h-11"><SelectValue placeholder="Not set" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none" data-testid="employment-none">Not set</SelectItem>
                {EMPLOYMENT_TYPES.map((t) => (
                  <SelectItem key={t.key} value={t.key} data-testid={`employment-${t.key}`}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Priority">
            <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
              <SelectTrigger data-testid="form-priority" className="rounded-none h-11"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PRIORITIES.map((p) => (
                  <SelectItem key={p.key} value={p.key} data-testid={`priority-opt-${p.key}`}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Status">
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger data-testid="form-status" className="rounded-none h-11"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUSES.map((s) => (
                  <SelectItem key={s.key} value={s.key} data-testid={`status-opt-${s.key}`}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>

        <Field label="Application URL">
          <Input data-testid="form-url" value={form.url} onChange={set("url")} placeholder="https://…" className="rounded-none h-11" />
        </Field>
        <Field label="Job description">
          <Textarea data-testid="form-description" value={form.description} onChange={set("description")} rows={6} placeholder="Paste the job description here — used for AI tailoring" className="rounded-none" />
        </Field>
        <Field label="Notes (private — not shown in shared links)">
          <Textarea data-testid="form-notes" value={form.notes} onChange={set("notes")} rows={3} placeholder="Recruiter name, referral, why you applied…" className="rounded-none" />
        </Field>

        <div className="flex items-center justify-between pt-2">
          {editing ? (
            <Button type="button" variant="ghost" onClick={remove} data-testid="form-delete" className="rounded-none text-red-600 hover:text-red-700 hover:bg-red-50">
              <Trash2 size={16} className="mr-1" /> Delete
            </Button>
          ) : <span />}
          <Button type="submit" data-testid="form-save" disabled={saving} className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white h-11 px-6">
            {saving ? "Saving…" : editing ? "Save changes" : "Create job"}
          </Button>
        </div>
      </form>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <Label className="label-tiny block mb-2">{label}</Label>
    {children}
  </div>
);
